<template>
  <NavigationMenuRoot
    class="flex flex-col md:w-[280px] shrink-0 px-3 group/nav data-[collapsed=true]:md:w-20 data-[collapsed=true]:md:px-2 md:transition-[width]"
    :data-collapsed="isCollapsed && !selector?.open && !isSettingsOverview"
    :orientation="maxSmall ? 'horizontal' : 'vertical'"
  >
    <!-- https://github.com/radix-ui/primitives/discussions/1874 -->
    <div
      class="max-md:h-[72px] flex md:flex-col grow md:my-3 md:rounded-xl md:overflow-y-auto [&>div:nth-child(1)]:contents md:[&>div:nth-child(2)]:contents group-data-[collapsed=true]/nav:md:gap-1 group-data-[collapsed=true]/nav:md:items-center md:border md:border-alpha-white-20 md:bg-alpha-black-10"
      :class="{
        'md:gap-4': !isSettingsOverview,
      }"
    >
      <div v-if="!maxSmall">
        <template v-if="isSettingsOverview">
          <div class="p-4">
            <div
              @click="$emit('go-back')"
              class="flex gap-2 items-center text-white pb-2 cursor-pointer"
            >
              <Icon name="back-arrow" size="xl" />
              <div class="font-medium">
                {{ $t("admin.navigation.settings.label") }}
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col">
            <BuildingSelector
              ref="selector"
              :organisation="organisation"
              :buildings="buildings"
            />
          </div>
        </template>
      </div>
      <NavigationMenuList
        v-show="!selector?.open"
        class="flex md:flex-col max-md:items-center justify-between grow md:gap-4 group-data-[collapsed=true]/nav:md:w-full group-data-[collapsed=false]/nav:md:px-4 group-data-[collapsed=true]/nav:md:px-[9px]"
      >
        <template v-if="maxSmall">
          <NavItem
            v-for="item in primary.items"
            :key="item.id"
            v-bind="item"
            :showTooltip="showTooltip"
          />
        </template>
        <div v-else class="flex flex-col grow">
          <NavGroup
            v-for="group in primary"
            :key="group.id"
            :label="group.label"
            :items="group.items"
            :showTooltip="showTooltip"
          />
        </div>
        <Component
          v-if="!isEmpty(secondary)"
          :is="maxSmall ? NavItem : 'div'"
          :icon="secondary.icon"
          :title="secondary.label"
          :active="secondary.active"
          :showTooltip="showTooltip"
        >
          <div class="flex flex-col gap-4 md:gap-2">
            <Component
              :is="maxSmall ? 'div' : NavDivider"
              class="flex flex-col grow max-md:px-2 md:pt-2 gap-0.5 group-data-[collapsed=true]/nav:md:gap-1 group-data-[collapsed=true]/nav:md:pt-3"
            >
              <template v-for="item in secondary.items">
                <NavItem
                  v-if="item.id"
                  :key="item.id"
                  v-bind="item"
                  :variant="maxSmall ? 'white' : 'on-brand'"
                  :showTooltip="showTooltip"
                  @click="emits[item.id] ? $emit(emits[item.id]) : null"
                />
              </template>
            </Component>
            <NavDivider
              class="flex flex-col pt-4 px-4 md:pb-4 md:px-0.5 justify-center group-data-[collapsed=true]/nav:md:px-0 group-data-[collapsed=true]/nav:md:pt-3"
            >
              <div
                class="flex items-center grow gap-2 w-full group-data-[collapsed=true]/nav:flex-col cursor-pointer max-w-full mb-4 md:mb-0"
              >
                <UserProfileItem
                  :user="user"
                  :inDrawer="maxSmall"
                  class="min-w-0 overflow-hidden"
                  data-id="nav.user-profile"
                  @click="$emit('openProfile')"
                />
                <Icon
                  :data-id="isCollapsed ? 'nav.expand' : 'nav.collapse'"
                  :name="isCollapsed ? 'right-side' : 'left-side'"
                  variant="fill"
                  size="custom"
                  class="hidden md:block w-12 h-12 shrink-0 p-3 text-alpha-white-50 hover:text-alpha-white-100 hover:bg-alpha-white-10 rounded-md cursor-pointer"
                  @click="handleCollapseChange"
                />
              </div>
            </NavDivider>
          </div>
        </Component>
      </NavigationMenuList>
    </div>
  </NavigationMenuRoot>
</template>

<script setup>
// Import Vue Utilites
import { ref, computed } from "vue";

// Import Utilites
import { storeToRefs } from "pinia";
import { isEmpty } from "lodash";

// Import Components
import { NavigationMenuList, NavigationMenuRoot } from "radix-vue";
import { Icon } from "@/SharedComponents/Icon";
import {
  BuildingSelector,
  NavGroup,
  NavItem,
  NavDivider,
  UserProfileItem,
} from "@/SharedComponents/Navigation";

// Composables
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useCollapse } from "@/Composables/useCollapse";

// Refs
const selector = ref(null);

// Collapse
const collapseStore = useCollapse("navigation");
const { isCollapsed } = storeToRefs(collapseStore);
const { handleCollapseChange } = collapseStore;

// Breakpoints
const { maxSmall } = useBreakpoints();

// Emits
const emits = {
  notifications: "openNotifications",
};

// Emits
defineEmits(["openNotifications", "openProfile"]);

// Computed props
const showTooltip = computed(() => {
  return !maxSmall.value && isCollapsed.value && !props.isSettingsOverview;
});

// Define Props
const props = defineProps({
  user: { type: Object, required: false },
  organisation: { type: String, required: true },
  buildings: { type: Array, default: () => [] },
  primary: { type: [Array, Object], required: false },
  isSettingsOverview: { type: Boolean, default: false },
  secondary: { type: Object, required: false },
});
</script>
